import React, { useRef } from 'react';
import {
  AppBar,
  Button,
  Toolbar,
  makeStyles,
  Box,
} from '~/lib/bn-material-ui';
import { useStoreValues } from '~/utility/BinSentry-ui-utility';
import navTheme from './nav-styles';
import { useBnMUIVariants } from '~/utility/styles/bn-mui-variants';
import classNames from 'classnames';
import useAccountFeatures from '~/utility/use-account-features';

const useStyles = makeStyles(() => {
  return {
    appBar: {
      zIndex: navTheme.nav.zIndex + 1,
      backgroundColor: navTheme.nav.appBar.backgroundColor,
    },
    appBarToolbar: {
      minHeight: navTheme.nav.appBar.minHeight,
      padding: '0px 20px',
      justifyContent: 'space-between',
    },
    binSentryButton: {
      padding: '6px 10px',
    },
    spacer: {
      flex: 1,
    },
    hidden: {
      display: 'none',
    },
    selectorContainer: {
      marginRight: '0.5em',
    },
  };
});

function BnAppBar() {
  const styles = useStyles();
  const bnMuiVariants = useBnMUIVariants();
  const { currentOrgHref, menuHidden, query = {} } = useStoreValues(['currentOrgHref', 'menuHidden', 'query']);

  const focusMode = [true, 'true'].includes(query.focusMode);
  const searchboxRef = useRef();
  const clickCountRef = useRef(0);
  const clickTimeoutRef = useRef(null);

  const { features, loading } = useAccountFeatures({ organizationId: currentOrgHref });

  const printMode = [true, 'true'].includes(query.printMode);
  if (printMode || menuHidden) {
    return null;
  }

  const showColdWarning = features?.coldWeatherAlert && !loading && !focusMode;

  // DEMO: Cleanup function when tapping the logo many times
  const performCleanup = async () => {
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName)),
        );
        console.log('All caches cleared');
      }

      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister()),
        );
        console.log('All service workers unregistered');
      }

      // Clear localStorage
      localStorage.clear();
      console.log('localStorage cleared');

      // Clear sessionStorage as well
      sessionStorage.clear();
      console.log('sessionStorage cleared');

      console.log('Reloading page');
      window.location.reload();
    } catch (error) {
      console.error('Error during cleanup:', error);
      // Still refresh the page even if cleanup fails
      window.location.reload();
    }
  };

  const requiredClicks = 5;
  const handleLogoClick = (e) => {
    e.preventDefault();

    const isOnHomePage = window.location.pathname === '/';

    if (!isOnHomePage) {
      // If not on home page, just navigate to home
      window.location.href = '/';
      return;
    }

    // Increment click count
    clickCountRef.current += 1;

    // Clear existing timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    // If this is the 5th click, perform cleanup
    if (clickCountRef.current >= requiredClicks) {
      console.log(`${requiredClicks} clicks detected - performing cleanup`);
      clickCountRef.current = 0;
      performCleanup();
      return;
    }

    // Set timeout to reset click count after 2 seconds
    clickTimeoutRef.current = setTimeout(() => {
      clickCountRef.current = 0;
      console.log('Click count reset');
    }, 2500);

    console.log(`Click count: ${clickCountRef.current}/${requiredClicks}`);
  };

  return (
    <AppBar classes={{ root: styles.appBar }}>
      <Toolbar classes={{ root: styles.appBarToolbar }}>
        <Button variant='contained' onClick={handleLogoClick} className={classNames(bnMuiVariants.containedButtonLight, styles.binSentryButton)}>
          <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
            <img src='/images/binsentry_shield_white.svg' alt='BinSentry Logo' height='35'/>
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <img src='/images/binsentry_logo_white.svg' alt='BinSentry Logo'/>
          </Box>
        </Button>

        <Box sx={{ display: 'flex', gap: '1em' }}>
          <Button variant='contained' href={'/inventory'} className={classNames(bnMuiVariants.outlinedButtonLight)}>
          ProSense HD Demo
          </Button>
          <Button variant='contained' href={'/feed-videos'} className={classNames(bnMuiVariants.outlinedButtonLight)}>
          ProSense Feed Demo
          </Button>
          <Button variant='contained' href={'/resources'} className={classNames(bnMuiVariants.outlinedButtonLight)}>
          Learn More
          </Button>
        </Box>

        {/* Reserved for future: selectors, search, and profile menu */}
      </Toolbar>
    </AppBar>
  );
}
export default BnAppBar;
